import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { FaEllipsisV } from 'react-icons/fa';

const ActionMenuContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const MenuButton = styled.button`
  background: var(--bg-light);
  border: 1px solid var(--gray-300);
  border-radius: var(--border-radius-md);
  padding: var(--spacing-sm);
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  
  &:hover {
    background: var(--gray-100);
    border-color: var(--primary-color);
    transform: translateY(-1px);
  }
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(44, 62, 80, 0.2);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  svg {
    color: var(--text-muted);
    font-size: var(--font-size-sm);
    transition: color 0.2s ease;
  }
  
  &:hover svg {
    color: var(--primary-color);
  }
`;

const MenuDropdown = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  background: var(--bg-light);
  border: 1px solid var(--gray-200);
  border-radius: var(--border-radius-md);
  box-shadow: var(--shadow-xl);
  z-index: 9999;
  min-width: 160px;
  padding: var(--spacing-xs) 0;
  margin-top: var(--spacing-xs);
  opacity: 0;
  transform: translateY(-8px);
  animation: slideIn 0.2s ease forwards;
  
  @keyframes slideIn {
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const MenuItem = styled.button`
  width: 100%;
  background: none;
  border: none;
  padding: var(--spacing-sm) var(--spacing-lg);
  text-align: left;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  font-size: var(--font-size-sm);
  color: var(--text-primary);
  font-family: var(--font-family);
  
  &:hover {
    background: var(--gray-100);
    padding-left: calc(var(--spacing-lg) + 4px);
  }
  
  &.danger:hover {
    background: var(--danger-color);
    color: var(--text-light);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  svg {
    font-size: var(--font-size-sm);
    width: 16px;
    flex-shrink: 0;
  }
`;

const MenuLink = styled(Link)`
  width: 100%;
  background: none;
  border: none;
  padding: var(--spacing-sm) var(--spacing-lg);
  text-align: left;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  font-size: var(--font-size-sm);
  color: var(--text-primary);
  font-family: var(--font-family);
  text-decoration: none;
  
  &:hover {
    background: var(--gray-100);
    padding-left: calc(var(--spacing-lg) + 4px);
    color: var(--text-primary);
    text-decoration: none;
  }
  
  &.danger:hover {
    background: var(--danger-color);
    color: var(--text-light);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    pointer-events: none;
  }
  
  svg {
    font-size: var(--font-size-sm);
    width: 16px;
    flex-shrink: 0;
  }
`;

const MenuDivider = styled.div`
  height: 1px;
  background: var(--gray-200);
  margin: var(--spacing-xs) 0;
`;

const GenericActionMenu = ({ 
  actions = [], 
  buttonLabel = "Opções",
  size = "md"
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);

  const buttonSizes = {
    sm: { width: '28px', height: '28px', padding: 'var(--spacing-xs)' },
    md: { width: '32px', height: '32px', padding: 'var(--spacing-sm)' },
    lg: { width: '36px', height: '36px', padding: 'var(--spacing-md)' }
  };

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const handleAction = (action) => {
    if (action.onClick && !action.disabled) {
      action.onClick();
    }
    setIsOpen(false);
  };

  const handleLinkClick = () => {
    setIsOpen(false);
  };

  // Fechar menu quando clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  return (
    <ActionMenuContainer ref={menuRef}>
      <MenuButton 
        onClick={toggleMenu} 
        aria-label={buttonLabel}
        style={buttonSizes[size]}
      >
        <FaEllipsisV />
      </MenuButton>
      
      {isOpen && (
        <MenuDropdown>
          {actions.map((action, index) => (
            <React.Fragment key={action.key || index}>
              {action.type === 'divider' ? (
                <MenuDivider />
              ) : action.to ? (
                <MenuLink
                  to={action.to}
                  onClick={handleLinkClick}
                  className={action.variant === 'danger' ? 'danger' : ''}
                  title={action.tooltip}
                >
                  {action.icon && <action.icon />}
                  {action.label}
                </MenuLink>
              ) : (
                <MenuItem
                  onClick={() => handleAction(action)}
                  disabled={action.disabled}
                  className={action.variant === 'danger' ? 'danger' : ''}
                  title={action.tooltip}
                >
                  {action.icon && <action.icon />}
                  {action.label}
                </MenuItem>
              )}
            </React.Fragment>
          ))}
        </MenuDropdown>
      )}
    </ActionMenuContainer>
  );
};

export default GenericActionMenu;
